/**
 * Auth Store - Zustand
 * Hanterar autentiseringstillstånd
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiGet, apiPut, apiPost } from '@/services/api-client';
import { logActivity } from '@/lib/activity-logger';
import { AuthRefreshError } from '@/services/auth-service';

const devLog = {
  info: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.info(...args);
  },
  warn: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.warn(...args);
  },
  error: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.error(...args);
  },
};

const IS_RUNTIME_PROD = (import.meta.env.VITE_ENVIRONMENT || '').toLowerCase() === 'prod';

export interface User {
  id: string;
  email: string;
  displayName: string;
  displayNameLastChangedAt?: string; // Senaste datum visningsnamn ändrades
  createdAt?: string;
  familyId?: string | null;
  familyRole?: 'owner' | 'member' | null;
  subscriptionTier: 'free' | 'premium';
  subscriptionActivatedAt?: string | null;
  paddleSubscriptionId?: string;
  storeType?: 'apple' | 'google' | 'web';
  termsAcceptedAt?: string; // ISO timestamp när användaren godkände användarvillkor
  termsVersion?: string; // Version av användarvillkoren som accepterades (e.g., "2026-02-09")
  onboardingCompleted: boolean;
  hasSeenRecommendationsModal?: boolean; // Has user opened personliga rekommendationer modal
  readArticles?: Array<{
    articleId: string;
    areaKey: string;
    readAt: string;
    pointsAwarded: number;
  }>;
  settings: {
    language: 'sv' | 'en';
    householdDisplayName?: string;
    notifications: {
      vmaAlerts: boolean;
      checklistReminders: boolean;
      expirationWarnings: boolean;
      weeklyDigest: boolean;
    };
    privacy: {
      shareStatsWithFamily: boolean;
      allowAnonymousAnalytics: boolean;
    };
  };
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  expiresAt: number | null; // Unix timestamp (ms) när accessToken går ut
  idToken: string | null; // ID token from OAuth provider (for logout)
  isAuthenticated: boolean;
  _hasHydrated: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null, expiresIn?: number) => void;
  login: (user: User, token: string, expiresIn?: number) => void;
  logout: () => Promise<void>;
  logoutLocal: () => void;
  setHasHydrated: (hasHydrated: boolean) => void;
  updateUser: (updates: Partial<Pick<User, 'displayName' | 'settings'>>) => Promise<{ success: boolean; error?: string }>;
  fetchUser: () => Promise<void>;
  enablePremium: () => Promise<boolean>;
  refreshAccessToken: () => Promise<TokenRefreshResult>;
  disablePremium: () => Promise<boolean>;
}

export type TokenRefreshResult = 'success' | 'retry' | 'session-ended' | 'user-deleted' | 'unavailable';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      expiresAt: null,
      idToken: null,
      isAuthenticated: false,
      _hasHydrated: false,

      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      setAccessToken: (token, expiresIn) => {
        const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;
        set({
          accessToken: token,
          expiresAt,
        });
      },

      login: (user, token, expiresIn) => {
        const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;
        // Prefer ephemeral idToken from sessionStorage; migrate legacy localStorage value
        let idToken: string | null = null;
        try {
          idToken = sessionStorage.getItem('idToken');
          if (!idToken) {
            const legacyIdToken = localStorage.getItem('idToken');
            if (legacyIdToken) {
              idToken = legacyIdToken;
              sessionStorage.setItem('idToken', legacyIdToken);
              localStorage.removeItem('idToken');
            }
          }
        } catch {
          idToken = null;
        }
        set({
          user,
          accessToken: token,
          expiresAt,
          idToken,
          isAuthenticated: true,
        });

        // Log login activity
        logActivity({ action: 'LOGIN', category: 'auth', description: 'User logged in', metadata: { userId: user.id, email: user.email } });

        // Clear dashboard loading flag so loading screen appears on next visit
        try {
          sessionStorage.removeItem('dashboardDataLoaded');
        } catch {
          // ignore if sessionStorage not available
        }

        // Best-effort: refresh server settings after login on this device.
        (async () => {
          try {
            const mod = await import('./settings-store');
            const s = mod.useSettingsStore.getState();
            if (typeof s.fetchFromServer === 'function') {
              await s.fetchFromServer();
            }
          } catch {
            // ignore - non-critical
          }

          // Fetch prep test results and recommendations from server
          // If user is in a family, pass familyId to get owner's data
          try {
            const prepMod = await import('./prep-test-store');
            const p = prepMod.usePrepTestStore.getState();
            if (typeof p.fetchFromServer === 'function') {
              // Get familyId from user data to fetch owner's prep test for family members
              const familyId = (user && (user.familyId as string | undefined)) || undefined;
              await p.fetchFromServer(familyId);
            }
          } catch {
            // ignore - non-critical
          }

          // Also fetch authoritative family state from server after login
          try {
            const famMod = await import('./family-store');
            const familyId = (user && (user.familyId as string | undefined)) || undefined;
            if (familyId && typeof famMod.useFamilyStore.getState().fetchFamily === 'function') {
              await famMod.useFamilyStore.getState().fetchFamily(familyId);
            }
          } catch (e) {
            // best-effort
            devLog.warn('[Auth] Failed to fetch family after login', e);
          }

          // Register push token if available (from React Native WebView)
          try {
            // Check both window.expoPushToken and localStorage
            let token = typeof window !== 'undefined' ? window.expoPushToken : null;
            
            if (!token && typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
              try {
                token = localStorage.getItem('expoPushToken');
                if (token) devLog.info('[Auth] Found push token in localStorage');
              } catch (e) {
                devLog.warn('[Auth] Could not read from localStorage:', e);
              }
            }
            
            const hasToken = !!token;
            devLog.info('[Auth] Checking for push token...', {
              hasWindow: typeof window !== 'undefined',
              hasExpoPushToken: hasToken,
              hasToken,
            });
            
            if (hasToken) {
              const { registerPushToken } = await import('../services/api-client');
              devLog.info('[Auth] Registering push token with backend...');
              
              const result = await registerPushToken(token!);
              
              if (result.success) {
                devLog.info('[Auth] Push token registered successfully after login');
              } else {
                devLog.warn('[Auth] Failed to register push token:', result.error);
              }
            } else {
              devLog.info('[Auth] No push token available to register');
            }
          } catch (e) {
            // best-effort
            devLog.error('[Auth] Exception while registering push token after login:', e);
          }
        })();
      },

      logout: async () => {
        try {
          const mod = await import('../services/auth-service');
          await mod.authService.logout();
        } catch {
          // best effort: continue with local/session cleanup and IdP logout redirect
        }

        // Log logout activity before clearing state
        const currentUser = get().user;
        if (currentUser) {
          logActivity({ action: 'LOGOUT', category: 'auth', description: 'User logged out', metadata: { userId: currentUser.id } });
        }

        // Aggressively clear storage so no traces of the session remain.
        try {
          // Notify other tabs first so they can respond before we clear everything
          try {
            localStorage.setItem('hemberedskapsappen-logout', Date.now().toString());
            localStorage.removeItem('hemberedskapsappen-logout');
          } catch { void 0; }

          // Clear both storages used by the app
          try { localStorage.clear(); } catch { void 0; }
          try { sessionStorage.clear(); } catch { void 0; }
        } catch { void 0; }

        // Get idToken before clearing state (needed for Entra logout)
        const idToken = get().idToken;

        // Clear in-memory state
        set({
          user: null,
          accessToken: null,
          expiresAt: null,
          idToken: null,
          isAuthenticated: false,
          _hasHydrated: true, // Keep hydrated flag true so HomePage can render
        });

        // Finally, redirect to the identity provider logout endpoint.
        // This MUST happen synchronously after clearing storage to prevent
        // persist middleware from saving state again before redirect.
        const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
        const postLogout = `${appUrl}/?logged_out=true`;
        
        const b2cTenant = import.meta.env.VITE_MICROSOFT_B2C_TENANT || '';
        const b2cPolicy = import.meta.env.VITE_MICROSOFT_B2C_POLICY || import.meta.env.VITE_MICROSOFT_B2C_USER_FLOW || '';
        const authHost = import.meta.env.VITE_MICROSOFT_B2C_AUTH_HOST || '';

        if (b2cTenant && b2cPolicy) {
          // CIAM pattern
          if (authHost === 'ciamlogin.com') {
            const tenantId = import.meta.env.VITE_MICROSOFT_B2C_TENANT_ID || '';
            if (tenantId) {
              let endSession = `https://${b2cTenant}.${authHost}/${tenantId}/oauth2/v2.0/logout?p=${encodeURIComponent(
                b2cPolicy
              )}&post_logout_redirect_uri=${encodeURIComponent(postLogout)}`;
              // Add id_token_hint if available (required for automatic logout)
              if (idToken) {
                endSession += `&id_token_hint=${encodeURIComponent(idToken)}`;
              }
              window.location.assign(endSession);
              return;
            }
          }

          // Default B2C pattern
          const tenantDomain = import.meta.env.VITE_MICROSOFT_B2C_TENANT_DOMAIN || `${b2cTenant}.onmicrosoft.com`;
          let endSession = `https://${authHost || 'login.microsoftonline.com'}/${tenantDomain}/oauth2/v2.0/logout?p=${encodeURIComponent(
            b2cPolicy
          )}&post_logout_redirect_uri=${encodeURIComponent(postLogout)}`;
          // Add id_token_hint if available (required for automatic logout)
          if (idToken) {
            endSession += `&id_token_hint=${encodeURIComponent(idToken)}`;
          }
          window.location.assign(endSession);
          return;
        }

        // Fallback: just redirect to app root
        window.location.assign(postLogout);
      },
      logoutLocal: () => {
        void import('../services/auth-service').then((mod) => mod.authService.logout()).catch(() => void 0);

        // Clear storages and in-memory state but do NOT redirect to identity provider.
        try {
          try { localStorage.setItem('hemberedskapsappen-logout', Date.now().toString()); localStorage.removeItem('hemberedskapsappen-logout'); } catch { void 0; }
          try { localStorage.clear(); } catch { void 0; }
          try { sessionStorage.clear(); } catch { void 0; }
        } catch { void 0; }

        set({ user: null, accessToken: null, expiresAt: null, idToken: null, isAuthenticated: false, _hasHydrated: true });
      },

      setHasHydrated: (hasHydrated) =>
        set({
          _hasHydrated: hasHydrated,
        }),

      updateUser: async (updates) => {
        const result = await apiPut<User>('/users/me', updates);
        if (result.success && result.data) {
          // Uppdatera user state med data från servern
          set({ user: result.data });

          try {
            const familyId = result.data.familyId;
            if (familyId) {
              const familyMod = await import('./family-store');
              if (typeof familyMod.useFamilyStore.getState().fetchFamily === 'function') {
                await familyMod.useFamilyStore.getState().fetchFamily(familyId);
              }
            }
          } catch (error) {
            devLog.warn('[Auth] Failed to refresh family after profile update', error);
          }

          return { success: true };
        }
        return { success: false, error: result.error };
      },

      refreshAccessToken: async () => {
        try {
          const refreshCsrfCookieName = import.meta.env.VITE_REFRESH_CSRF_COOKIE_NAME || 'bh_refresh_csrf';
          const hasLegacyRefreshToken = typeof window !== 'undefined' ? !!localStorage.getItem('refreshToken') : false;
          const hasStoredRefreshCsrfToken = typeof window !== 'undefined' ? !!localStorage.getItem('refreshCsrfToken') : false;
          const hasRefreshCsrfCookie = typeof document !== 'undefined'
            ? document.cookie.split('; ').some((cookie) => cookie.startsWith(`${encodeURIComponent(refreshCsrfCookieName)}=`))
            : false;

          if (!hasLegacyRefreshToken && !hasStoredRefreshCsrfToken && !hasRefreshCsrfCookie) {
            if (get().isAuthenticated) {
              get().logoutLocal();
              return 'session-ended';
            }
            return 'unavailable';
          }

          const mod = await import('../services/auth-service');
          const legacyRefreshToken = typeof window !== 'undefined' ? localStorage.getItem('refreshToken') : null;
          const resp = await mod.authService.refreshTokenWithRetry(legacyRefreshToken || undefined);
          
          if (resp && resp.accessToken) {
            const expiresAt = resp.expiresIn ? Date.now() + resp.expiresIn * 1000 : null;
            set({ accessToken: resp.accessToken, expiresAt });
            
            if ((resp as unknown as { user?: unknown }).user) {
              const u = (resp as unknown as { user?: unknown }).user as User;
              set({ user: u });
            }
            
            if (resp.refreshToken) {
              try {
                if (IS_RUNTIME_PROD) localStorage.removeItem('refreshToken');
                else localStorage.setItem('refreshToken', resp.refreshToken);
              } catch { void 0; }
            }
            
            return 'success';
          }
          return 'retry';
        } catch (e) {
          devLog.warn('[Auth] Token refresh failed', e);
          if (e instanceof AuthRefreshError && e.kind !== 'retryable') {
            get().logoutLocal();
            if (e.kind === 'user-deleted' && typeof window !== 'undefined') {
              window.location.replace('/');
              return 'user-deleted';
            }
            return 'session-ended';
          }
          return 'retry';
        }
      },

      // Convenience: enable premium locally and on server
      // When enabling, set subscriptionActivatedAt to now if enabling
      enablePremium: async () => {
        const now = new Date().toISOString();
        const result = await apiPut<User>('/users/me', { subscriptionTier: 'premium', subscriptionActivatedAt: now });
        if (result.success && result.data) {
          set({ user: result.data });

          // If we have a refresh token, try to refresh tokens so the access token
          // carries the updated subscriptionTier claim — do this silently.
          const success = await useAuthStore.getState().refreshAccessToken();
          if (success !== 'success') {
            devLog.warn('[Auth] Silent refresh after enabling premium failed');
          }

          // Reload page to ensure all state, cache, and Premium features are synchronized
          window.location.reload();

          return true;
        }
        return false;
      },
      disablePremium: async () => {
        const user = get().user;

        if (user?.storeType === 'apple' || user?.storeType === 'google') {
          devLog.warn('[Auth] Refusing local premium disable for store-managed subscription', {
            storeType: user.storeType,
          });
          return false;
        }

        // Om prenumerationen k\u00f6ptes via Paddle \u2014 avsluta via Paddle API f\u00f6rst
        if (user?.paddleSubscriptionId) {
          const cancelResult = await apiPost<{ cancelled: boolean }>('/premium/paddle-cancel', {});
          if (!cancelResult.success) {
            devLog.warn('[Auth] Paddle cancel failed', cancelResult);
            // Forts\u00e4tt \u00e4nd\u00e5 \u2014 Paddle schemal\u00e4gger avslut, webhook uppdaterar DB
            // men vi vill inte blockera anv\u00e4ndaren om API-anropet misslyckas
          }
        }

        const result = await apiPut<User>('/users/me', { subscriptionTier: 'free' });
        if (result.success && result.data) {
          set({ user: result.data });
          // Ensure prepGoal reverts to '7d' when subscription is downgraded.
          try {
            // Use dynamic import to avoid circular imports at module load time
            const mod = await import('./settings-store');
            const s = mod.useSettingsStore.getState();
            if (s.prepGoal !== '7d') {
              s.setPrepGoal('7d');
            }
          } catch { void 0; 
            // Do not throw — this is best-effort
            devLog.warn('[Auth] Failed to reset prepGoal on downgrade');
          }
          // Clear family state when premium is disabled
          try {
            const famMod = await import('./family-store');
            famMod.useFamilyStore.getState().setFamily(null);
            famMod.useFamilyStore.getState().setIsOwner(false);
            famMod.useFamilyStore.getState().setPendingInvitations([]);
            famMod.useFamilyStore.getState().setSentInvitations([]);
          } catch {
            devLog.warn('[Auth] Failed to clear family state on downgrade');
          }
          return true;
        }
        return false;
      },

      fetchUser: async () => {
        const result = await apiGet<User>('/users/me');
        if (result.success && result.data) {
          // Use setUser to trigger knowledge store sync
          const setUserFn = get().setUser;
          setUserFn(result.data);
          
          // After fetching user on a newly authenticated device, refresh
          // settings from server so we don't accidentally sync stale local
          // settings back and overwrite the authoritative values.
          try {
            const mod = await import('./settings-store');
            const s = mod.useSettingsStore.getState();
            if (typeof s.fetchFromServer === 'function') {
              await s.fetchFromServer();
            }
          } catch (e) {
            // best-effort
            devLog.warn('[Auth] Failed to fetch settings after fetchUser', e);
          }
          // Fetch prep test results and recommendations from server
          // If user is in a family, pass familyId to get owner's data
          try {
            const prepMod = await import('./prep-test-store');
            const p = prepMod.usePrepTestStore.getState();
            if (typeof p.fetchFromServer === 'function') {
              // Get familyId from fetched user data to fetch owner's prep test for family members
              const familyId = (result.data && (result.data.familyId as string | undefined)) || undefined;
              await p.fetchFromServer(familyId);
            }
          } catch (e) {
            // best-effort
            devLog.warn('[Auth] Failed to fetch prep test after fetchUser', e);
          }
          // Also always refresh family state from server to ensure client
          // uses the authoritative family membership and doesn't rely on
          // stale local values.
          try {
            const famMod = await import('./family-store');
            const familyId = (result.data && (result.data.familyId as string | undefined)) || undefined;
            if (familyId && typeof famMod.useFamilyStore.getState().fetchFamily === 'function') {
              await famMod.useFamilyStore.getState().fetchFamily(familyId);
            }
          } catch (e) {
            devLog.warn('[Auth] Failed to fetch family after fetchUser', e);
          }
        }
      },
    }),
    {
      name: 'hemberedskapsappen-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        expiresAt: state.expiresAt,
        idToken: state.idToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
          (async () => {
            try {
              state?.setHasHydrated(true);
              
              // Check if we just logged out (redirected from Entra with ?logged_out=true)
              if (typeof window !== 'undefined' && window.location.search.includes('logged_out=true')) {
                // Clear any persisted state and don't attempt to restore session
                try { localStorage.clear(); } catch { void 0; }
                try { sessionStorage.clear(); } catch { void 0; }
                state?.setUser(null);
                state?.setAccessToken(null);
                // Remove the query param from URL
                window.history.replaceState({}, document.title, window.location.pathname);
                return;
              }
              
              // If we don't have an access token, try one refresh during rehydration
              // using secure cookie flow (or legacy token if present).
              const currentAccess = state?.accessToken;
              if (!currentAccess) {
                try {
                  await state?.refreshAccessToken();
                } catch { 
                  // Vid fel under automatisk refresh vid sidladdning,
                  // rensa endast lokal session utan att gå via Entra-logout.
                  state?.logoutLocal(); 
                }
              }
            } catch { void 0; }
          })();
        },
    }
  )
);

// Helper hook för att kolla hydration status
export const useAuthHydrated = () => useAuthStore((state) => state._hasHydrated);

// Expose auth store on window for React Native WebView access
if (typeof window !== 'undefined') {
  (window as any).authStore = useAuthStore;
}
